import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { evalKeys } from '@/lib/queryKeys'
import type { CreateChatCaseInput, CreateChatCaseResult } from '../types'
import { createEvalAssertion, createEvalCase } from '../api'

const NO_AUTH_TOKEN_ERROR = 'No authentication token available'

// Creates the chat eval case, then (best-effort) attaches a single pass-criteria
// assertion. If the case is created but the assertion fails, the case still
// stands — we surface `assertionFailed` so the UI can warn instead of erroring.
export function useCreateEvalCaseMutation() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      caseInput,
      assertion,
    }: CreateChatCaseInput): Promise<CreateChatCaseResult> => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)

      const created = await createEvalCase(token, caseInput)

      if (!assertion || !created?.id) {
        return { created, assertionFailed: false }
      }

      try {
        await createEvalAssertion(token, created.id, assertion)
        return { created, assertionFailed: false }
      } catch (error) {
        return {
          created,
          assertionFailed: true,
          assertionError:
            error instanceof Error ? error.message : 'Unknown error',
        }
      }
    },
    onSuccess: () => {
      // Only the duty's test-case list needs refreshing; the case detail page
      // fetches its own assertions/runs on mount.
      void queryClient.invalidateQueries({
        queryKey: evalKeys.testCasesAll(organization?.id),
      })
    },
  })
}
